import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import InputAdornment from "@material-ui/core/InputAdornment";

// @material-ui/icons
import Search from "@material-ui/icons/Search";
import Category from "@material-ui/icons/Category";
import Edit from "@material-ui/icons/Edit";
import Archive from "@material-ui/icons/Archive";
import Unarchive from "@material-ui/icons/Unarchive";
import Visibility from "@material-ui/icons/Visibility";
import Add from "@material-ui/icons/Add";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

// category API functions
import { getCategories, updateCategory } from "../../api";

// table components
import CategoryTable from "./CategoryTable";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

function CategoryManagement(props) {
  const { classes, user } = props;
  const history = useHistory();
  
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Filter categories when search text or show archived changes
  useEffect(() => {
    filterCategories();
  }, [categories, searchText, showArchived]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await getCategories();
      const categoryData = response.data;
      setCategories(categoryData);
    } catch (error) {
      console.error("Failed to load categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Recursively filter categories including children
  const filterCategoryTree = (category, searchTerm, includeArchived) => {
    // Check if this category matches the search
    const matchesSearch = !searchTerm || category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesArchiveFilter = includeArchived || category.enabled !== false;

    // Filter children recursively
    let filteredChildren = [];
    if (category.children && category.children.length > 0) {
      filteredChildren = category.children
        .map(child => filterCategoryTree(child, searchTerm, includeArchived))
        .filter(child => child !== null);
    }

    // Include this category if:
    // 1. It matches the search and archive filter, OR
    // 2. It has children that match (to preserve tree structure)
    if ((matchesSearch && matchesArchiveFilter) || filteredChildren.length > 0) {
      return {
        ...category,
        children: filteredChildren
      };
    }

    return null;
  };

  const filterCategories = () => {
    const searchTerm = searchText.toLowerCase().trim();
    
    const filtered = categories
      .map(category => filterCategoryTree(category, searchTerm, showArchived))
      .filter(category => category !== null);

    setFilteredCategories(filtered);
  };

  const handleArchiveCategory = async (category, enabled) => {
    try {
      await updateCategory(category._id, { ...category, enabled });
      await loadCategories();
    } catch (error) {
      console.error("Failed to update category:", error);
    }
  };

  const handleEditCategory = (category) => {
    history.push(`/categories/edit/${category._id}`, {
      categories: categories,
      selectedCategory: category
    });
  };

  const handleCreateCategory = () => {
    history.push('/categories/edit', {
      categories: categories
    });
  };

  const handleViewCategory = (category) => {
    // Open category URL in new tab
    if (category.url_path) {
      window.open(`https://saucey.com/${category.url_path}`, '_blank');
    }
  };

  // Recursively collect all category IDs for expand/collapse all
  const getAllCategoryIds = (categories) => {
    const ids = new Set();
    const traverse = (cats) => {
      cats.forEach(cat => {
        if (cat.children && cat.children.length > 0) {
          ids.add(cat._id);
          traverse(cat.children);
        }
      });
    };
    traverse(categories);
    return ids;
  };

  const handleExpandAll = () => {
    const allIds = getAllCategoryIds(filteredCategories);
    setExpandedCategories(allIds);
  };

  const handleCollapseAll = () => {
    setExpandedCategories(new Set());
  };

  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      setSearchText(event.target.value);
    }
  };

  const handleSearchIconClick = () => {
    const searchInput = document.getElementById('category-search');
    setSearchText(searchInput.value);
  };


  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <Category />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Category Management
            </h4>
            <div style={{ position: 'absolute', right: '20px', top: '20px' }}>
              <Button color="success" size="sm" onClick={handleCreateCategory}>
                <Add /> Create New Category
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {/* Search and Filter Controls */}
            <GridContainer style={{ marginBottom: '20px' }}>
              <GridItem xs={12} sm={6} md={4}>
                <CustomInput
                  id="category-search"
                  formControlProps={{
                    className: classes.top + " " + classes.search,
                    fullWidth: true
                  }}
                  inputProps={{
                    placeholder: "Search categories...",
                    inputProps: {
                      "aria-label": "Search",
                      id: "category-search"
                    },
                    onKeyUp: handleSearch,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Search 
                          className={classes.searchIcon}
                          onClick={handleSearchIconClick}
                          style={{ cursor: 'pointer' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </GridItem>
              <GridItem xs={12} sm={6} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showArchived}
                      onChange={(e) => setShowArchived(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Include Archived Categories"
                  style={{ marginTop: '10px' }}
                />
              </GridItem>
            </GridContainer>

            {/* Category Table */}
            <CategoryTable
              categories={filteredCategories}
              loading={loading}
              expandedCategories={expandedCategories}
              onToggleExpand={(categoryId) => {
                const newExpanded = new Set(expandedCategories);
                if (newExpanded.has(categoryId)) {
                  newExpanded.delete(categoryId);
                } else {
                  newExpanded.add(categoryId);
                }
                setExpandedCategories(newExpanded);
              }}
              onEdit={handleEditCategory}
              onArchive={handleArchiveCategory}
              onView={handleViewCategory}
            />
          </CardBody>
        </Card>
      </GridItem>

    </GridContainer>
  );
}

export default connect(
  state => ({
    user: state.app.user,
  })
)(withStyles(styles)(CategoryManagement));
